import { jsx as _jsx } from "react/jsx-runtime";
import React, { createContext, useContext, useMemo, useRef } from "react";
import { isWidgetTestEnvironment } from "../utils/widgetHelpers";
const HOUR_MS = 3_600_000;
const CACHE_TTL_MS = 30_000;
const MAX_CACHE_ENTRIES = 50;
const DEFAULT_REQUEST_TIMEOUT = 15_000;
function roundDown(ts, granularity) {
    return Math.floor(ts / granularity) * granularity;
}
function roundUp(ts, granularity) {
    return Math.ceil(ts / granularity) * granularity;
}
/**
 * Cross-chart request coordinator.
 *
 * - Global max inflight: 4 requests across all charts
 * - Per-adapter rate limit: token bucket (2 requests / 500ms sliding window)
 * - Cross-chart dedup via coarse key (symbol|tf|rounded-hour-from|rounded-hour-to)
 *   for historical data; exact timestamps used when `to` is within 1h of "now"
 *   to prevent stale current-candle from cache.
 * - Near-now requests (to within 1h of now) bypass cache entirely — current
 *   candle is mutable and will be updated by WS ticker_update.
 * - Subscriber tracking: only abort inflight when ALL subscribers have unmounted
 * - Result cache with 30s TTL + LRU eviction (max 50 entries)
 */
export class RequestCoordinator {
    maxGlobalInflight = 4;
    perAdapterTokens = 2;
    inflight = new Map();
    subscribers = new Map();
    cache = new Map();
    adapterTokenBuckets = new Map();
    pendingQueue = [];
    processing = false;
    makeKey(params) {
        // DATA-03: the data-source scope is part of the key so different adapters
        // never share a cache entry or an in-flight dedup slot.
        const scope = params.scope || "default";
        // When `to` is within 1 hour of "now", use exact timestamps to prevent
        // stale current-candle data caused by hour-rounding collision.
        const now = Date.now();
        const toMs = params.to.getTime();
        if (now - toMs < HOUR_MS) {
            return `${scope}|${params.symbol}|${params.timeframe}|${params.from.getTime()}|${toMs}`;
        }
        const coarsedFrom = roundDown(params.from.getTime(), HOUR_MS);
        const coarsedTo = roundUp(toMs, HOUR_MS);
        return `${scope}|${params.symbol}|${params.timeframe}|${coarsedFrom}|${coarsedTo}`;
    }
    getAdapterLabel(params) {
        return `${params.scope || "default"}::${params.symbol}@${params.timeframe}`;
    }
    checkAdapterTokens(adapterLabel) {
        // Tests use mock adapters (no real network) and assert synchronously —
        // deferring fetches onto macrotask retry timers would starve them
        // (the harness only flushes microtasks). Bypass gating in test env.
        if (isWidgetTestEnvironment())
            return true;
        const now = Date.now();
        const bucket = this.adapterTokenBuckets.get(adapterLabel) ?? { tokens: this.perAdapterTokens, lastRefill: now };
        const elapsed = now - bucket.lastRefill;
        if (elapsed >= 500) {
            bucket.tokens = this.perAdapterTokens;
            bucket.lastRefill = now;
        }
        if (bucket.tokens <= 0)
            return false;
        bucket.tokens--;
        this.adapterTokenBuckets.set(adapterLabel, bucket);
        return true;
    }
    evictStaleCache() {
        const now = Date.now();
        const staleKeys = [];
        for (const [key, entry] of this.cache) {
            if (now - entry.timestamp > CACHE_TTL_MS) {
                staleKeys.push(key);
            }
        }
        for (const key of staleKeys) {
            this.cache.delete(key);
        }
        if (this.cache.size > MAX_CACHE_ENTRIES) {
            const entries = [...this.cache.entries()].sort((a, b) => a[1].timestamp - b[1].timestamp);
            const toRemove = entries.slice(0, this.cache.size - MAX_CACHE_ENTRIES);
            for (const [key] of toRemove) {
                this.cache.delete(key);
            }
        }
    }
    processQueue() {
        if (this.processing)
            return;
        this.processing = true;
        this.pendingQueue.sort((a, b) => b.priority - a.priority);
        while (this.pendingQueue.length > 0 && this.inflight.size < this.maxGlobalInflight) {
            const next = this.pendingQueue.shift();
            if (!next)
                break;
            if (this.cache.has(next.key)) {
                next.resolve(this.cache.get(next.key).data);
                continue;
            }
            if (this.inflight.has(next.key)) {
                // Same key became inflight while queued (rate-limit retry path) —
                // requeue instead of dropping: dropping abandons resolve/reject
                // and hangs the caller's promise forever.
                this.pendingQueue.unshift(next);
                setTimeout(() => this.processQueue(), 100);
                break;
            }
            // DATA-03: use the stored label; deriving it from `key` broke once the
            // scope prefix was added to the key.
            const adapterLabel = next.adapterLabel;
            if (!this.checkAdapterTokens(adapterLabel)) {
                this.pendingQueue.unshift(next);
                setTimeout(() => this.processQueue(), 100);
                break;
            }
            next.execute();
        }
        this.processing = false;
    }
    enqueue(fetchFn, params, priority, callerId, signal) {
        // Early exit if the caller's signal is already aborted — prevents stale
        // pending entries during StrictMode double-mount (mount→cleanup→mount).
        if (signal?.aborted) {
            return Promise.reject(new DOMException("Aborted", "AbortError"));
        }
        const key = this.makeKey(params);
        // Current candle (within 1h of "now") must never be served from cache — it is mutable.
        const isNearNow = Date.now() - params.to.getTime() < HOUR_MS;
        this.evictStaleCache();
        if (!isNearNow) {
            const cached = this.cache.get(key);
            if (cached) {
                return Promise.resolve(cached.data);
            }
        }
        const existingInflight = this.inflight.get(key);
        if (existingInflight) {
            if (!this.subscribers.has(key)) {
                this.subscribers.set(key, new Set());
            }
            this.subscribers.get(key).add(callerId);
            if (signal) {
                signal.addEventListener("abort", () => {
                    const subs = this.subscribers.get(key);
                    if (subs) {
                        subs.delete(callerId);
                        if (subs.size === 0) {
                            existingInflight.abortController.abort();
                            this.inflight.delete(key);
                            this.subscribers.delete(key);
                        }
                    }
                }, { once: true });
            }
            return existingInflight.promise;
        }
        return new Promise((resolve, reject) => {
            const execute = () => {
                const abortController = new AbortController();
                const adapterLabel = this.getAdapterLabel(params);
                if (signal) {
                    signal.addEventListener("abort", () => {
                        const subs = this.subscribers.get(key);
                        if (subs) {
                            subs.delete(callerId);
                            if (subs.size === 0) {
                                abortController.abort();
                                this.inflight.delete(key);
                                this.subscribers.delete(key);
                            }
                        }
                        reject(new DOMException("Aborted", "AbortError"));
                    }, { once: true });
                }
                if (!this.subscribers.has(key)) {
                    this.subscribers.set(key, new Set());
                }
                this.subscribers.get(key).add(callerId);
                const timeoutId = setTimeout(() => {
                    abortController.abort();
                    this.inflight.delete(key);
                    this.subscribers.delete(key);
                    reject(new Error(`RequestCoordinator timeout after ${DEFAULT_REQUEST_TIMEOUT}ms: ${key}`));
                }, DEFAULT_REQUEST_TIMEOUT);
                const promise = fetchFn(abortController.signal).then((data) => {
                    clearTimeout(timeoutId);
                    // Don't cache near-now responses — current candle is mutable and will be
                    // updated by WS ticker_update; caching it causes stale last-candle bug.
                    if (!isNearNow) {
                        this.cache.set(key, { data, timestamp: Date.now() });
                    }
                    this.inflight.delete(key);
                    resolve(data);
                    return data;
                }, (err) => {
                    clearTimeout(timeoutId);
                    this.inflight.delete(key);
                    reject(err);
                    throw err;
                });
                // The rethrow above keeps the error visible to late joiners via
                // existingInflight.promise — but with no joiner it surfaces as an
                // "Uncaught (in promise)" (first seen with HTTP 504 on wide intraday
                // ranges). Mark it handled; joiners still observe the rejection.
                promise.catch(() => { });
                this.inflight.set(key, { promise: promise, abortController, timestamp: Date.now() });
            };
            this.pendingQueue.push({
                key,
                adapterLabel: this.getAdapterLabel(params),
                execute,
                resolve: (value) => resolve(value),
                reject,
                priority,
                enqueueTime: Date.now(),
            });
            this.processQueue();
        });
    }
    getInflightCount() {
        return this.inflight.size;
    }
    getPendingCount() {
        return this.pendingQueue.length;
    }
    reset() {
        for (const [, entry] of this.inflight) {
            entry.abortController.abort();
        }
        this.inflight.clear();
        this.subscribers.clear();
        this.pendingQueue = [];
        // Rate-limit buckets + result cache are cross-test pollution vectors
        // via the module-level fallbackCoordinator — a reset must clear them.
        this.adapterTokenBuckets.clear();
        this.cache.clear();
    }
}
const RequestCoordinatorContext = createContext(null);
export function useRequestCoordinator() {
    const ctx = useContext(RequestCoordinatorContext);
    if (!ctx) {
        return fallbackCoordinator;
    }
    return ctx.coordinator;
}
const fallbackCoordinator = new RequestCoordinator();
/**
 * Test isolation hook: reset the module-level fallback coordinator between
 * tests. Contract tests render without a RequestCoordinatorProvider (like
 * single-chart embeds), so they share this instance — without a reset,
 * token-bucket + cache state leaks across tests and starves later fetches.
 */
export function resetFallbackRequestCoordinatorForTests() {
    fallbackCoordinator.reset();
}
export function RequestCoordinatorProvider({ children }) {
    const coordinatorRef = useRef(null);
    const value = useMemo(() => {
        if (!coordinatorRef.current) {
            coordinatorRef.current = new RequestCoordinator();
        }
        return { coordinator: coordinatorRef.current };
    }, []);
    return (_jsx(RequestCoordinatorContext.Provider, { value: value, children: children }));
}
